import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./ui/use-toast";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Ciao! Sono l'assistente virtuale di Mangimi Rubini. Come posso aiutarti oggi?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    
    // Validate message length
    if (userMessage.length > 2000) {
      toast({
        title: "Messaggio troppo lungo",
        description: "Il messaggio non può superare 2000 caratteri",
        variant: "destructive",
      });
      return;
    }
    
    // Limit conversation history
    if (messages.length > 50) {
      toast({
        title: "Conversazione troppo lunga",
        description: "Riavvia la chat per continuare",
        variant: "destructive",
      });
      return;
    }
    
    setInput("");
    
    const newMessages: Message[] = [
      ...messages,
      { role: "user", content: userMessage },
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("chat-assistant", {
        body: { messages: newMessages },
      });

      if (error) {
        console.error("Error calling chat-assistant:", error);
        throw error;
      }

      const assistantMessage = data?.choices?.[0]?.message?.content;
      
      if (assistantMessage) {
        setMessages([
          ...newMessages,
          { role: "assistant", content: assistantMessage },
        ]);
      } else {
        throw new Error("Nessuna risposta ricevuta");
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Errore",
        description: "Si è verificato un errore. Prova a scrivere su WhatsApp!",
        variant: "destructive",
      });
      
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: "Mi dispiace, si è verificato un errore. Ti consiglio di contattarci direttamente su WhatsApp al +39 075 848314 per ricevere assistenza immediata!",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const openWhatsApp = () => {
    window.open("https://wa.me/390758483140", "_blank");
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50 bg-primary hover:bg-primary/90"
          aria-label="Apri chat assistente"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[380px] h-[600px] bg-card border border-border rounded-2xl shadow-xl z-50 flex flex-col overflow-hidden animate-fade-in-up">
          {/* Header */}
          <div className="bg-primary text-white p-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">Assistente Mangimi Rubini</h3>
              <p className="text-sm text-white/80">Online ora</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      message.role === "user"
                        ? "bg-primary text-white"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted text-foreground rounded-2xl px-4 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* WhatsApp Button */}
          <div className="px-4 py-2 border-t border-border">
            <Button
              onClick={openWhatsApp}
              variant="outline"
              className="w-full text-sm"
            >
              💬 Scrivici su WhatsApp
            </Button>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                type="submit"
                size="icon"
                disabled={isLoading || !input.trim()}
                className="bg-primary hover:bg-primary/90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatBot;
